<template>
  <div class="flex flex-col lg:flex-row lg:items-center lg:justify-between">
    <h2 class="heading-2">
      {{ listingsCount }}
    </h2>

    <div class="mt-4 space-y-12 md:space-y-0 lg:mt-0 lg:flex lg:gap-12">
      <RevInputSelect
        id="number-of-listing-per-page"
        v-model="selectedListingsCount"
        class="lg:min-w-[14rem]"
        :label="i18n(translations.listingCountLabel)"
        :options="numberOfListingsPerPageChoices"
        @v-model:change="handleSelectedListingsCountChange"
      />

      <RevInputSelect
        v-if="!hasOnlyOneActiveMarket"
        id="toggleListingDisplay"
        v-model="toggleListingDisplay"
        class="lg:min-w-[14rem]"
        :label="i18n(translations.displayTypes)"
        :options="displayTypes"
        :value="toggleListingDisplay"
        @update:model-value="
          $emit('listing-display-change', toggleListingDisplay)
        "
      />

      <RevInputSelect
        v-if="!hasOnlyOneMarket"
        id="referenceMarket"
        v-model="referenceMarketCountryCode"
        class="lg:min-w-[14rem]"
        :label="i18n(translations.referenceMarket)"
        :options="activeMarketsOptions"
        :value="referenceMarketCountryCode"
        @update:model-value="
          $emit('reference-market-change', referenceMarketCountryCode)
        "
      />

      <RevInputSelect
        id="sortBy"
        v-model="sortBy"
        class="lg:min-w-[14rem]"
        :label="i18n(translations.sortBy)"
        :options="sortByOptions"
        @v-model:change="handleSortByChange"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'

import type { MarketCountryCode } from '@backmarket/http-api/src/api-models/MarketCountryCode'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useLocalStorage } from '@backmarket/utils/composables/useLocalStorage'
import { RevInputSelect } from '@ds/components/InputSelect'

import { useSellerInfoStore } from '~/scopes/auth/stores/sellerInfoStore'
import {
  PUBLICATION_STATE,
  STORAGE_VIEW_TYPE_NAME,
} from '~/scopes/listings/Listings.constants'

import {
  LISTINGS_PER_PAGE_COUNT_OPTIONS,
  SORT_OPTIONS,
  VIEWS,
} from './Ordering.constants'
import translations from './Ordering.translations'

const { activeMarkets, hasOnlyOneMarket, hasOnlyOneActiveMarket } =
  useSellerInfoStore()
const i18n = useI18n()

const props = defineProps<{
  activePublicationState: string
  count: number
  listingsPerPage: string
  sortBy: string
  referenceMarket: MarketCountryCode | ''
}>()

const emit = defineEmits<{
  'update:listings-per-page': [value: string]
  'update:sort-by': [value: string]
  'listing-display-change': [value: string]
  'reference-market-change': [value: string]
  'sort-by-change': [value: string]
}>()

const listingsCount = computed(() => {
  let translationKey
  switch (props.activePublicationState) {
    case PUBLICATION_STATE.MISSING_PRICE_OR_COMMENT:
      translationKey = translations.missingInfosListingsCount
      break
    case PUBLICATION_STATE.IN_VALIDATION:
      translationKey = translations.inValidationListingsCount
      break
    case PUBLICATION_STATE.DISABLED:
      translationKey = translations.archivedListingsCount
      break
    default:
      translationKey = translations.activeListingsCount
      break
  }

  return i18n(translationKey, { offersTotalCount: props.count })
})

const localStorage = useLocalStorage()
const selectedListingsCount = computed({
  get: () => props.listingsPerPage,
  set: (value) => emit('update:listings-per-page', value),
})
const toggleListingDisplay = ref(
  localStorage.getItem(STORAGE_VIEW_TYPE_NAME) === VIEWS.DETAILED
    ? VIEWS.DETAILED
    : VIEWS.SIMPLE,
)
const referenceMarketCountryCode = ref(props.referenceMarket || '')
const sortBy = computed({
  get: () => props.sortBy,
  set: (value) => emit('update:sort-by', value),
})

const displayTypes = [
  { label: i18n(translations.simpleView), value: VIEWS.SIMPLE },
  { label: i18n(translations.detailedView), value: VIEWS.DETAILED },
]
const numberOfListingsPerPageChoices = LISTINGS_PER_PAGE_COUNT_OPTIONS.map(
  (value) => ({ label: value.toString(), value: value.toString() }),
)
const activeMarketsOptions = activeMarkets.map((market: MarketCountryCode) => ({
  label: i18n.country(market),
  value: market,
}))

const sortByOptions = SORT_OPTIONS.map((option) => ({
  ...option,
  label: i18n(option.label),
}))

function handleSelectedListingsCountChange(value: string) {
  selectedListingsCount.value = value
}

function handleSortByChange(value: string) {
  sortBy.value = value
}
</script>
